import { defineCopy } from '@floorfile/i18n'

/**
 * Parts, vendors and the stock ledger (localization plan Phase 5, batch 5c).
 *
 * The slice is large enough that it lands in three chunks — `5c-2` (vendors),
 * `5c-3` (the parts register) and `5c-4` (the part detail page and the stock
 * components). This file grows with each of them; the chunk records in
 * `LOCALIZATION_IMPLEMENTATION_PLAN.md` say what arrived when.
 *
 * ## `parts` owns four enums, and one of them lived in a module constant
 *
 * `VENDOR_CATEGORY_LABELS` was a `Record<VendorCategory, string>` exported from
 * `components/VendorFieldset.tsx` — the first defect class, and the reason it
 * survives so long is that it looks like the right thing: it is typed against
 * the contract enum, it is shared by all three call sites, and the copy lint
 * rule cannot see a module constant at all. It is a catalogue group now, still
 * keyed `Record<VendorCategory, string>`, so an added member fails the build
 * here rather than rendering `SCREAMING_CASE` on the vendor register.
 *
 * The other three (`PartTxnType`, `ReorderStatus`, and the part preview's
 * `ARCHIVED`) were reaching the screen RAW, which no linter and no English
 * review can catch — see the chunk records.
 *
 * ## `ServiceType` is NOT owned here
 *
 * `VendorProfilePanel` renders a service-history badge, and `equipment` already
 * owns `ServiceType` (`equipmentCopy.serviceType`). One owner per enum: this
 * slice reads those words rather than restating them, exactly as `audits` reads
 * `formsCopy`. The vendor profile's `vendorType` stays raw because it is a
 * contract `z.string()`, not an enum — free text a company typed.
 */
export const partsCopy = defineCopy('parts', {
  offlineAction: {
    stockManualAdjustment: 'Stock manual adjustment ({quantity})',
    stockReorder: 'Stock reorder ({quantity})',
    stockReturn: 'Stock return ({quantity})',
    stockDamaged: 'Stock damaged ({quantity})',
    stockLost: 'Stock lost ({quantity})',
    requestReorder: 'Request re-order',
  },
  // ── vendors (chunk 5c-2) ───────────────────────────────────────────────

  /**
   * `VendorCategory`. Was `VENDOR_CATEGORY_LABELS` in `VendorFieldset.tsx`,
   * read by the register's filter, the register's badge, the detail page's
   * subtitle and the fieldset's own `<select>` — four surfaces, one constant no
   * translator could reach.
   */
  vendorCategory: {
    PARTS_SUPPLIER: 'Parts supplier',
    SERVICE: 'Service',
    MANUFACTURER: 'Manufacturer',
    OTHER: 'Other',
  },

  /** /vendors — the register. */
  vendors: {
    title: 'Vendors',
    description: 'Who you buy from and who services your machines.',
    importCsv: 'Import CSV',
    newVendor: '+ New vendor',
    searchLabel: 'Search vendor, contact, or email',
    searchPlaceholder: 'Search vendor, contact, or email…',
    categoryFilterLabel: 'Vendor category',
    allCategories: 'All categories',
    activityFilterLabel: 'Vendor activity',
    /** F31: the chip reads "Supplies: PB-100" when a part's Vendors card links here. */
    suppliesFilterLabel: 'Supplies',
    activeAndInactive: 'Active and inactive',
    activeOnly: 'Active only',
    inactiveOnly: 'Inactive only',
    inactive: 'Inactive',
    quickView: 'Quick view',
    closeQuickView: 'Close quick view',
    /** The register (owner finding 2026-09-18: the vendors list was a stack of cards with no header; now a `DataTable` like every other register). */
    column: {
      name: 'Name',
      category: 'Category',
      contact: 'Contact',
      email: 'Email',
      phone: 'Phone',
      status: 'Status',
      added: 'Added',
    },
    active: 'Active',
    quickActive: 'Active',
    quickAll: 'All vendors',
    region: 'Vendors',
    rowMenu: 'Actions for {name}',
    openRecord: 'Open',
    vendorSortField: { name: 'name', category: 'category', createdAt: 'date added' },
    emptyTitle: 'No vendors yet',
    emptyDescription: 'Add your suppliers so parts carry lead times and preferred sources.',
    pagerLabel: 'Vendor pages',
    /**
     * One message rather than four fragments. It was
     * `Page {p} of {n} · {t} vendors` assembled in JSX, so a translator got
     * "Page", "of" and "vendors" separately and no way to reorder them — and
     * `vendors` never inflected for a total of one.
     */
    pageSummary: {
      one: 'Page {page} of {pages} · {count} vendor',
      other: 'Page {page} of {pages} · {count} vendors',
    },
    previous: 'Previous',
    next: 'Next',
  },

  /** The create dialog. */
  newVendor: {
    title: 'New vendor',
    cancel: 'Cancel',
    create: 'Create',
    failed: 'Could not create.',
  },

  /** The edit dialog. */
  editVendor: {
    /** `{name}` is the vendor's own name — a placeholder, not a concatenation. */
    title: 'Edit {name}',
    cancel: 'Cancel',
    save: 'Save changes',
    saving: 'Saving…',
    failed: 'Could not save the vendor.',
  },

  /** The shared create/edit fieldset. */
  vendorForm: {
    name: 'Name*',
    category: 'Category',
    contactPerson: 'Contact person',
    phone: 'Phone',
    email: 'Email',
    emailHint: 'Used for supplier reorder notes',
    website: 'Website',
    websiteHint: 'https://…',
    /** FF-AUDIT-072: the contract's format checks, said under the field. */
    phoneInvalid: 'Enter a phone number — digits, with + ( ) - and spaces (for example +1 902 555 0100).',
    websiteInvalid: 'Enter a web address (for example https://example.com).',
    address: 'Address',
    notes: 'Notes',
    active: 'Active — inactive vendors stay on record but drop out of pickers',
  },

  /** /vendors/:id. */
  vendorDetail: {
    back: '← Back to vendors',
    loading: 'Loading…',
    missingTitle: 'Vendor unavailable',
    missingDescription: 'The vendor identifier is missing.',
    notFoundTitle: 'Vendor not found',
    notFoundDescription: 'It may have been archived or removed.',
    inactive: 'Inactive',
    edit: 'Edit details',
    detailsHeading: 'Details',
    /**
     * Two whole sentences rather than a stem plus a tail.
     *
     * It was `No contact details recorded yet{canManage ? ' — add the phone
     * number and address your team needs.' : '.'}` — a translator would have
     * received a clause with no verb and a lone full stop, and French cannot
     * always attach a continuation where English can.
     */
    noContact: 'No contact details recorded yet.',
    noContactManage:
      'No contact details recorded yet — add the phone number and address your team needs.',
    contact: 'Contact',
    phone: 'Phone',
    email: 'Email',
    website: 'Website',
    address: 'Address',
    notes: 'Notes',
  },

  /** The purchasing-profile panel, shared by the quick view and the page. */
  vendorProfile: {
    loading: 'Loading…',
    tablistLabel: 'Vendor profile',
    /**
     * Tab LABELS keyed by the tab's own id. The ids used to BE the labels —
     * `useState<'Overview' | 'Parts supplied' | …>` and `tab === 'Overview'` —
     * so translating them would have changed the comparison the panel switches
     * on. Same split the `equipment` and `parts` tab strips already use.
     */
    tabs: {
      parts: 'Parts supplied',
      contracts: 'Equipment contracts',
      service: 'Service history',
      quality: 'Quality',
    },
    noneLinked: 'None linked yet.',
    preferred: '★ Preferred',
    noContracts: 'None.',
    /** F22: the Parts supplied and Equipment contracts registers. */
    partsRegion: 'Parts this vendor supplies',
    contractsRegion: 'Equipment this vendor supports',
    partsColumn: {
      partNumber: 'Part #',
      name: 'Name',
      /** The vendor's own number for the part, not ours. */
      supplierPartNumber: 'Supplier part #',
      unitCost: 'Unit cost',
      minimumOrderQty: 'Min. order qty',
      leadTime: 'Lead time (days)',
      preferred: 'Preferred',
    },
    contractsColumn: {
      equipment: 'Equipment',
      relationship: 'Relationship',
      contractNumber: 'Contract #',
      supportLevel: 'Support level',
      supportPhone: 'Support phone',
      supportEmail: 'Support email',
    },
    noServices: 'No services recorded for this vendor.',
    /** The service-history register (owner, 2026-09-23), modeled on
     * `equipmentCopy.history.column` — same column order. */
    column: {
      servicedAt: 'Date',
      type: 'Type',
      equipment: 'Equipment',
      description: 'Description',
      workOrder: 'Work order',
      cost: 'Cost',
    },
    /**
     * The whole amount is one message, so fr-CA can write `12,50 $` with the
     * symbol after the digits and a space before it. Same reasoning as
     * `downtimeCopy.stats.amount`, and NOT `formatCurrency` — there is no
     * `company_settings.currency` column, so naming a currency would be
     * inventing a company-wide fact.
     *
     * The DIGITS are still `.toFixed(2)`, which is the deferred number-format
     * sweep the plan records as debt. What this key buys is the seam: when the
     * sweep lands it changes what is PASSED, not the sentence around it.
     */
    money: '${amount}',
  },

  /** The published searchable vendor selector. */
  vendorPicker: {
    label: 'Vendor',
    placeholder: 'Search vendors…',
  },

  // ── the parts register (chunk 5c-3) ────────────────────────────────────

  /**
   * `PartTxnType`, the ledger's movement kind. Was
   * `<StatusBadge label={txn.type}>` — the raw contract token on every row of
   * the movement ledger, which is the record a plant reads when it wants to
   * know why a count changed.
   *
   * The part detail page's own movement table printed the SAME enum through
   * `txn.type.replaceAll('_', ' ')` instead, so the two tables already
   * disagreed in English (`MAINTENANCE_USAGE` vs `MAINTENANCE USAGE`). Both
   * read this group now.
   *
   * **`TRANSFER_OUT` / `TRANSFER_IN` were found by the compiler**, not by
   * reading. `PartTxnType` has nine members and the register's `TXN_TONES` map
   * lists seven, so an inter-facility transfer already rendered untoned — and
   * writing seven keys here failed the build rather than shipping two more raw
   * tokens on the one row type where telling the legs apart is the point
   * (PRT-5). That is the whole reason this group is a
   * `Record<PartTxnType, string>` and not a loose object.
   */
  txnType: {
    MAINTENANCE_USAGE: 'Maintenance usage',
    MANUAL_ADJUSTMENT: 'Manual adjustment',
    INITIAL_STOCK: 'Initial stock',
    REORDER: 'Reorder',
    RETURN: 'Return',
    DAMAGED: 'Damaged',
    LOST: 'Lost',
    TRANSFER_OUT: 'Transfer out',
    TRANSFER_IN: 'Transfer in',
  },

  /** `ReorderStatus`. Was `<StatusBadge label={request.status}>` — raw. */
  reorderStatus: {
    OPEN: 'Open',
    APPROVED: 'Approved',
    ORDERED: 'Ordered',
    RECEIVED: 'Received',
    DECLINED: 'Declined',
    CANCELLED: 'Cancelled',
  },

  /**
   * The status shown on the part hover card.
   *
   * `partSummary` (apps/api) emits exactly one status, `ARCHIVED`, and its
   * facts are numbers and free text — so this is the whole of the part's
   * contribution to `EntityPreview`'s enum table. Keyed on (entity type, field)
   * there, never on the field name alone: `previews` explains why.
   */
  /** F12 — `PartType`. The one owning catalogue: every screen reads it here. */
  partType: {
    CONSUMABLE: 'Consumable',
    CRITICAL: 'Critical',
    REPLACEMENT: 'Replacement part',
  },
  partTypeNone: 'Not classified',
  previewStatus: {
    ARCHIVED: 'Archived',
  },

  /** /parts — the register and its four tabs. */
  page: {
    title: 'Parts',
    description:
      'Stock counts you can trust without counting — every movement is a ledger line with a name on it.',
    tabsLabel: 'Parts views',
    /**
     * Tab LABELS keyed by the tab's own id. The ids used to BE the labels
     * (`TABS = ['Catalog', 'Low stock', …]`), with a second map turning them
     * into URL slugs — so the displayed words were simultaneously a display
     * string, a switch value and half of an address. They are opaque ids now
     * and only the label is translated.
     */
    tabs: {
      catalog: 'Catalog',
      lowStock: 'Low stock',
      reorders: 'Reorder requests',
      ledger: 'Ledger',
    },

    /**
     * Sortable-column names for the `role="status"` sort announcement.
     *
     * Every field in the contract's `PartSort` needs an entry, not only the
     * ones with a header: the default sort is announced first and `createdAt`
     * and `category` are selectable from the URL with no column of their own.
     */
    sortField: {
      partNumber: 'part number',
      name: 'name',
      category: 'category',
      currentStock: 'stock',
      createdAt: 'date added',
    },
    /** F28: the announcement when sorted by one facility's stock. */
    sortFacilityStock: 'stock at {code}',

    searchPlaceholder: 'Search number, name, manufacturer…',
    importCsv: 'Import CSV',
    newPart: '+ New part',
    /** The register's accessible name (the scroll region on a desktop, the card list on a phone). */
    scrollLabel: 'Parts',
    /**
     * Fiix audit Phase 1: the filter bar. The quick chips are the one
     * question a storeroom asks first; the panel fields are the vendor and
     * equipment hub links (H-01, M-04) and the dashboard's slow-moving
     * predicate, so a deep link and a hand-picked filter are the same state.
     */
    quickLow: 'Low stock',
    quickAll: 'All',
    filterEquipment: 'Equipment',
    filterSlowMoving: 'Slow-moving',
    selectAll: 'Select all visible parts',
    selectRow: 'Select {name}',

    colPartNumber: 'Part #',
    colName: 'Name',
    colBin: 'Bin',
    /**
     * F28: one facility's pair of columns, captioned with its CODE (the full
     * name is the header's hover text).
     */
    colFacilityStock: 'Stock ({code})',
    colFacilityBin: 'Bin ({code})',
    facilityHeaderTitle: '{name}',
    colTotal: 'Total',
    colStock: 'Stock',
    colStatus: 'Status',
    colVendor: 'Vendor',
    /** Column-chooser extras, hidden by default. */
    colCategory: 'Category',
    /** F12 */
    /** F10.4 */
    colAssigned: 'Assigned',
    colCommitted: 'Committed',
    colAvailable: 'Available',
    assignedHelp: 'Planned on open work orders and not yet used, whatever the date.',
    committedHelp: 'Due within {days} days: jobs under way, undated or due soon, plus what calendar plans will call for.',
    availableHelp: 'In stock {inStock} − committed {committed}.',
    shortBy: 'Short by {count}',
    colPartType: 'Type',
    filterType: 'Type',
    setType: 'Set type',
    setTypeTitle: 'Set the part type',
    setTypeBody: { one: 'For the {count} selected part.', other: 'For the {count} selected parts.' },
    setTypeSubmit: 'Apply',
    clearTypeFilter: 'Show every type',
    setTypeDone: '{updated} changed, {unchanged} already set, {failed} not changed.',
    colReorderPoint: 'Reorder point',
    colUnitCost: 'Unit cost',
    viewAll: 'View all',
    /**
     * Fiix audit Phase 0: the per-facility stock columns are behind a toggle
     * (MaintainX's "Show per location") rather than permanent — they were
     * crowding the identity columns into ~90 px and made every row 130 px.
     */
    showPerFacility: 'Show stock per facility',
    /** Row overflow menu — every row action lives here. */
    rowMenu: 'More actions for {partNumber}',
    openRecord: 'Open',

    /** The per-facility cell's low badge. */
    low: 'LOW',
    /** The Status column — the word beside the row's warning edge, so colour
     * is never the only channel. */
    statusLow: 'Low stock',
    /** FF-AUDIT-20260921-210633-038: the badge is per facility; the figure beside it is the company total. */
    statusLowAt: 'Low at {facilities}',
    statusInStock: 'In stock',
    /** `of {target}` and `/ reorder at {point}` were two JSX fragments glued
     * either side of a number. Whole phrases, so a translator can move them. */
    ofTarget: 'of {target}',
    reorderAt: '/ reorder at {point}',
    /** `ProgressMeter`'s accessible name and its spoken value. */
    meterLabel: '{partNumber} stock',
    meterNoTarget: '{stock} on hand, no target configured',
    meterOfTarget: '{stock} of {target}',
    meterOfTargetLow: '{stock} of {target}, at or below the reorder point',

    receiveStock: 'Receive stock',
    consumeStock: 'Consume stock',
    adjust: 'Adjust',
    requestReorder: 'Request re-order',
    /** The floating selection bar's sentence. The bar takes a FINISHED label:
     * a primitive that glues `s` onto a noun it was handed cannot inflect. */
    selected: { one: '{count} part selected', other: '{count} parts selected' },

    emptyTitle: 'No parts yet',
    emptyDescription: 'Register the storeroom to start the ledger.',

    /** Low-stock tab (register, owner 2026-09-23). */
    lowStockScrollLabel: 'Low stock',
    colOnHand: 'On hand',
    colReorderAt: 'Reorder at',
    colShortBy: 'Short by',
    /** F33: no live request for this part and facility — a plain stock-in. */
    addStockNoRequest: 'Add stock (no request)…',
    /** F33: the live request's own page. */
    openRequest: 'Open {number}',
    colSuggestedQty: 'Suggested qty',
    lowStockSuggested: ' · suggested order: {quantity}',
    lowStockVendor: ' · {vendor}',
    markReceived: 'Mark received…',
    lowEmptyTitle: 'Nothing is low',
    lowEmptyDescription: 'Every part is above its reorder point.',

    /** Reorder-requests tab. */
    configureReorder: 'Configure reorder settings →',

    /** Ledger tab. */
    partFilterLabel: 'Part filter',
    ledgerScrollLabel: 'Stock movements',
    colWhen: 'When',
    colFacility: 'Facility',
    colType: 'Type',
    colQty: 'Qty',
    colBeforeAfter: 'Before → after',
    /** F29: the ledger's reference and note are two columns now. */
    colReference: 'Reference',
    colNote: 'Note',
    /** Keyed on the ledger's `refType` — it used to print the raw code ("reorder_request"). */
    refTypeLabel: {
      reorder_request: 'Reorder request',
      work_order: 'Work order',
      facility_transfer: 'Transfer',
      manual: 'Manual entry',
    },
    refTypeOther: 'Other record',
    ledgerEmptyTitle: 'Empty ledger',
    ledgerEmptyDescription: 'Every stock movement lands here, signed.',
    loadingOlder: 'Loading…',
    loadOlder: 'Load older movements',
  },

  /** The create dialog. */
  newPart: {
    /** F12 */
    partType: 'Type',
    title: 'New part',
    partNumber: 'Part number',
    partNumberPlaceholder: 'Part number*',
    name: 'Name',
    namePlaceholder: 'Name*',
    bin: 'Default bin/shelf',
    unitCost: 'Unit cost $',
    /**
     * FF-AUDIT-20260921-210633-039: what the quantities count.
     *
     * Every number in the module was a bare integer — "7 in stock",
     * "reorder at 2", "Received 10" — with no unit anywhere, so a
     * storeroom stocking both fasteners and seal strip could not read its
     * own figures. Free text because plants stock in coils, pails and
     * skids; the suggestions are a starting point, not a list.
     */
    unitOfMeasure: 'Unit',
    unitOfMeasurePlaceholder: 'EA, kg, m, box…',
    /** The column is numeric(12,2): a third decimal was stored rounded, silently. */
    unitCostRounded: 'Unit cost is kept to two decimals — this will be stored as {value}.',
    minStock: 'Min stock',
    reorderAt: 'Reorder at',
    reorderQty: 'Reorder qty',
    /** Owner, 2026-09-22: settable at last, and it belongs to the facility the opening stock lands in. */
    targetStock: 'Target stock',
    initialStock: 'Initial stock',
    /** The three thresholds are a floor, a trigger and a ceiling. */
    thresholdHelp:
      'Min stock is the floor you never want to go below, Reorder at is what triggers a re-order, and Target stock is what you re-order back up to at this facility.',
    cancel: 'Cancel',
    create: 'Create',
    failed: 'Could not create.',
    /** FF-AUDIT-005: a negative quantity or cost is refused beside its field,
     * named — not as a toast about "highlighted fields" with nothing highlighted. */
    notNegative: 'Must be 0 or more.',
    invalid: 'This value is not valid.',
  },

  /** The two-step quantity adjustment. */
  adjust: {
    title: 'Adjust stock — {partNumber}',
    /** FF-AUDIT-054: field-level refusals. */
    quantitySign: 'Enter a positive quantity — the movement type decides whether stock goes up or down.',
    quantityZero: 'Quantity cannot be zero.',
    typeLabel: 'Adjustment type',
    /**
     * A DELIBERATE subset of `PartTxnType`, with its own words.
     *
     * `INITIAL_STOCK` and `MAINTENANCE_USAGE` are written by the system, never
     * chosen by a person, so the contract's `AdjustStockInput` accepts only
     * these five — deriving the list from `PartTxnType.options` would offer two
     * reasons the server rejects. The wording also differs on purpose: the
     * ledger badge reports what happened (`Reorder`) while this control asks
     * what you are doing and states the sign (`Received order (+)`).
     */
    reason: {
      REORDER: 'Received order (+)',
      MANUAL_ADJUSTMENT: 'Manual count correction (±, note required)',
      RETURN: 'Returned to stock (+)',
      DAMAGED: 'Damaged (−)',
      LOST: 'Lost (−)',
    },
    quantity: 'Quantity',
    facility: 'Facility',
    facilityDefault: 'Facility: my active facility',
    note: 'Note',
    notePlaceholder: 'Note…',
    noteRequiredPlaceholder: 'Note (required)…',
    cancel: 'Cancel',
    confirm: 'Confirm',
    failed: 'Could not adjust.',
  },

  /**
   * The reorder-request register (Fiix audit Phase 1) — one row per request,
   * the workflow actions in the row menu. Its filters share the page URL with
   * the catalogue, so they are the `rq…` keys in `part-filters.ts`.
   */
  request: {
    region: 'Reorder requests',
    searchPlaceholder: 'Search request, part, requester…',
    column: {
      number: 'Request #',
      part: 'Part',
      quantity: 'Qty',
      status: 'Status',
      facility: 'Facility',
      vendor: 'Vendor',
      requester: 'Requested by',
      reason: 'Reason',
      created: 'Raised',
    },
    /** Sortable-column names for the `role="status"` sort announcement. */
    requestSortField: {
      requestNumber: 'request number',
      part: 'part',
      quantity: 'quantity',
      status: 'status',
      createdAt: 'date raised',
    },
    /** The quick chips: what a purchaser asks first. */
    quick: {
      open: 'Open',
      ordered: 'Awaiting receipt',
      mine: 'Mine',
    },
    urgent: 'Urgent',
    auto: 'Auto',
    suggestedUnitCost: ' · ~${amount} ea',
    automatic: 'automatic',
    /** The purchaser's note, shown beside the reason once a decision exists. */
    decisionNote: 'Decision: {note}',
    rowMenu: 'More actions for {number}',
    openPart: 'Open part',
    approve: 'Approve',
    decline: 'Decline…',
    declinePrompt: 'Decline note (goes back to the requester):',
    /**
     * Titles for the shared dialogs that replaced `prompt()` and
     * `confirm()`. The decline note is sent back to the person who asked
     * for the part, so it is required rather than quietly optional.
     */
    declinePromptTitle: 'Decline this request',
    markOrdered: 'Mark ordered',
    sendToSupplier: 'Send order to supplier',
    markReceived: 'Mark received',
    /**
     * FF-AUDIT-20260921-210633-037: what actually turned up, before stock moves.
     * Booking the full requested quantity on one unconfirmed click meant a short
     * delivery could not be recorded and a mis-click invented stock.
     */
    receiveTitle: 'Receive {number}',
    receiveDescription: '{part} — {requested} requested. Enter what arrived.',
    receiveQuantity: 'Quantity received',
    receiveNote: 'Reference (optional)',
    receiveNotePlaceholder: 'Packing slip or PO number',
    receiveShort: '{short} short of the requested quantity — the rest is not booked in.',
    receiveInvalid: 'Enter a whole number of units, 1 or more.',
    receiveCancel: 'Cancel',
    receiveConfirm: 'Receive',
    /** The requester's (or a manager's) own withdrawal of an OPEN request. */
    cancel: 'Cancel request…',
    cancelConfirm: 'Cancel request {number}? It will no longer reach the purchaser.',
    cancelConfirmTitle: 'Cancel this request?',
    emptyTitle: 'No requests',
    emptyDescription:
      'Hit Request re-order on any part — it goes straight to whoever handles purchasing.',
    /**
     * F33 (OD-18): the workflow, its next step and partial deliveries.
     */
    explainer: 'Request → Approve → Order → Receive (receiving adds the stock).',
    nextStepColumn: 'Next step',
    receivedColumn: 'Received',
    receivedOf: '{received} / {total}',
    next: { approve: 'Approve', 'mark-ordered': 'Mark ordered', receive: 'Receive…' },
    quickFlow: { needsApproval: 'Needs approval', toOrder: 'To order', awaitingDelivery: 'Awaiting delivery' },
    openRequest: 'Open request',
    receiveAction: 'Receive…',
    receiveUnitPrice: 'Unit price paid (optional)',
    receiveUnitPriceHelp: 'What you actually paid per unit on this delivery.',
    receiveFacility: 'Facility',
    receiveCompletes: 'This completes the request.',
    receiveStillToCome: { one: '{count} more still to come after this.', other: '{count} more still to come after this.' },
    receiveTooMany: 'Only {remaining} still to come on this request.',
    receiveRemaining: '{received} of {total} already received.',
    closeShort: 'Close — nothing more coming…',
    closeShortTitle: 'Close this request short?',
    closeShortPrompt: 'Why is nothing more coming? (shown on the request)',
    error: {
      RECEIVE_CONFLICT: 'This request changed while you were receiving it — nothing was posted. Refresh and try again.',
      RECEIVE_EXCEEDS_REMAINING: 'That is more than is still to come on this request.',
      INVALID_TRANSITION: 'This request has already moved on. Refresh to see where it is.',
      NOTHING_RECEIVED: 'Nothing has arrived on this request yet — cancel it instead.',
    },
  },

  /** F33: the re-order request's own page. */
  requestPage: {
    back: '← Reorder requests',
    missingTitle: 'Request not found',
    missingDescription: 'It may be at a facility you cannot see, or the link is wrong.',
    loading: 'Loading the request…',
    stepsLabel: 'Progress',
    steps: { requested: 'Requested', approved: 'Approved', ordered: 'Ordered', received: 'Received' },
    stepDone: '{step} — done',
    stepCurrent: '{step} — current',
    stepTodo: '{step} — to do',
    stopped: { DECLINED: 'Declined', CANCELLED: 'Cancelled' },
    shortClosed: 'Closed short',
    detailsHeading: 'Details',
    part: 'Part',
    facility: 'Facility',
    quantity: 'Requested',
    received: 'Received',
    reason: 'Reason',
    decisionNote: 'Decision note',
    requester: 'Requested by',
    automatic: 'Automatic (low stock)',
    recipients: 'Recipients',
    vendor: 'Preferred vendor',
    urgent: 'Urgent',
    receiptsHeading: 'Deliveries',
    receiptsEmpty: 'Nothing has been received yet.',
    receiptColumn: { date: 'Date', quantity: 'Qty', price: 'Price paid', by: 'By', note: 'Note', ledger: 'Ledger' },
    ledgerLink: 'View',
    supplierEmailsHeading: 'Supplier e-mails',
    historyHeading: 'History',
    historyEmpty: 'No history yet.',
    /** The request's activity, by event type. `{actor}` is a person's name. */
    history: {
      create: '{actor} raised the request',
      'reorder_request.approved': '{actor} approved it',
      'reorder_request.declined': '{actor} declined it',
      'reorder_request.cancelled': '{actor} cancelled it',
      'reorder_request.ordered': '{actor} marked it ordered',
      'reorder_request.partially_received': '{actor} received part of the delivery',
      'reorder_request.received': '{actor} received the last of the delivery',
      'reorder_request.closed_short': '{actor} closed it short',
      other: '{actor} updated it',
    },
    someone: 'Someone',
  },


  /** The CSV importer, shared by parts and vendors. */
  csvImport: {
    titleParts: 'Import parts from CSV',
    titleVendors: 'Import vendors from CSV',
    description:
      'Preview validates every row first. Imports are capped at 500 rows and valid rows keep their individual result.',
    chooseFile: 'Choose a .csv file',
    csvData: 'CSV data',
    /** One sentence, three counts. It was three JSX fragments with the
     * separators between them, and the first fragment was a ternary between
     * two DIFFERENT sentences — so a translator saw five pieces. */
    summaryPreview: '{valid} valid · {errors} errors · {rows} rows',
    summaryImported: '{imported} imported · {errors} errors · {rows} rows',
    rowStatus: {
      VALID: 'Valid',
      IMPORTED: 'Imported',
      ERROR: 'Error',
    },
    row: 'Row {row}',
    rowIdentifier: ' · {identifier}',
    rowMessage: ' — {message}',
    close: 'Close',
    preview: 'Preview',
    importValid: 'Import valid rows',
    failed: 'Import failed.',
  },

  /** Settings → Parts & reordering. */
  settings: {
    title: 'Parts & reordering',
    description:
      'How low stock is handled, who receives reorder requests, and whether suppliers are emailed directly.',
    failed: 'Could not save the reorder settings.',

    /** F11: what a priced delivery does to the catalog cost (D20, default OFF). */
    /** F10.2: the committed window and the low-stock basis. */
    demandHeading: 'Assigned, committed and available',
    demandDescription:
      'In stock is what is on the shelf. Assigned is planned on open work orders and not yet used. Committed is the part of that due soon — jobs under way, undated or due within the window — plus what calendar plans will call for within the window. Available is In stock minus Committed.',
    windowLabel: 'Committed window (days)',
    windowHelp: '0 to 365. With 0, only jobs under way, undated or overdue count. Meter-based plans are not forecast; their open work orders still count.',
    windowInvalid: 'A whole number from 0 to 365.',
    saveWindow: 'Save',
    basisLabel: 'Judge low stock on',
    basis: {
      IN_STOCK: 'What is on the shelf (In stock)',
      AVAILABLE: 'What is left after commitments (Available)',
    },
    basisHelp: 'With Available, planning a job can put a part at its reorder point before any stock moves.',
    costOnReceiptHeading: 'Cost on receipt',
    costOnReceiptDescription:
      'When a delivery is received with the price paid, that price is always kept on the stock ledger. Choose whether it also becomes the part’s catalog cost.',
    costOnReceiptLabel: 'The catalog cost',
    costOnReceipt: {
      OFF: 'Stays as it is',
      LAST_PRICE: 'Becomes the last price paid',
    },
    lowStockHeading: 'Low stock',
    lowStockDescription: 'What happens when a part falls below its reorder point.',
    behaviorLabel: 'Automatic low-stock behavior',
    /** `LowStockBehavior`. The `<select>` is built from the contract enum. */
    behavior: {
      OFF: 'Do nothing',
      NOTIFY: 'Notify the responsible person',
      CREATE_REQUEST: 'Auto-create a reorder request',
    },

    recipientsHeading: 'Who receives reorder requests',
    recipientsDescription:
      'In-app users and/or plain email addresses (a buyer outside FloorFile, say). When a part has a steward, they can be preferred over the default recipients.',
    // Straight apostrophe, matching the `&apos;` the JSX rendered — this is a
    // string migration, not a typography pass.
    preferSteward: "Prefer the part's steward when one is set",
    recipientUsers: 'Recipient users',
    recipientEmails: 'Recipient email addresses',
    emailPlaceholder: 'buyer@example.com',
    add: 'Add',
    removeEmail: 'Remove {email}',

    supplierEmailHeading: 'Direct supplier email',
    supplierEmailDescription:
      'Off by default. When enabled, a reorder request can email the supplier directly — a reorder note, not a purchase order.',
    enableSupplierEmail: 'Enable direct supplier emails',
    templateLabel: 'Email template',
    templateHelp: 'Sent as the body of the supplier email. Leave empty for the default note.',
    saveTemplate: 'Save template',
    saved: 'Saved.',

    /** F12: who hears when a CRITICAL part runs low. */
    criticalHeading: 'Critical parts',
    criticalDescription:
      'When a part marked Critical runs low, these people and teams are alerted urgently — on top of the usual re-order recipients. Muting re-order notices does not silence it.',
    criticalAlertAtLabel: 'Alert when stock reaches',
    criticalAlertAt: {
      REORDER_POINT: 'The reorder point',
      MINIMUM_STOCK: 'The minimum stock',
      BOTH: 'Both (each alerts once)',
    },
    criticalUsers: 'People',
    criticalTeams: 'Teams',
    criticalEmails: 'Email addresses',
    noTeams: 'No teams yet — create them in Settings → Teams.',
    saveCritical: 'Save critical-part alerts',
  },

  // ── the part detail page and the stock components (chunk 5c-4) ─────────

  /** /parts/:id. */
  detail: {
    back: '← Back to parts',
    loading: 'Loading…',
    missingTitle: 'Part unavailable',
    missingDescription: 'The part identifier is missing.',
    notFoundTitle: 'Part not found',
    notFoundDescription: 'It may have been archived or removed.',
    /** `{partNumber} · {name}` — the separator is punctuation, the order is not. */
    heading: '{partNumber} · {name}',
    /** Three fragments glued in a template literal became three messages: the
     * stem, and two tails a language may want somewhere else entirely. */
    inStock: '{stock} in stock',
    /**
     * FF-AUDIT-20260921-210633-039: the unit, where the number is read.
     *
     * One message rather than `{stock} + ' ' + unit`, so a language that
     * puts the unit elsewhere (or needs no space) can say so.
     */
    inStockWithUnit: '{stock} {unit} in stock',
    quantityWithUnit: '{quantity} {unit}',
    reorderAtTail: ' · reorder at {point}',
    preferredTail: ' · preferred: {vendor}',
    lowStock: 'LOW STOCK',
    requestReorder: 'Request reorder',
    edit: 'Edit details',
    /** Fiix audit Phase 2: the record's primary action when the viewer may
     * move stock — the same dialog the register's row menu opens. */
    adjust: 'Adjust stock',
    /** The rail (`PropertyList`): the catalog facts the header does not carry. */
    facts: {
      region: 'Part details',
      partNumber: 'Part number',
      manufacturer: 'Manufacturer',
      category: 'Category',
      /** F12 */
      partType: 'Type',
      storageLocation: 'Default storage location',
      minimumStock: 'Minimum stock',
      reorderPoint: 'Reorder point',
      reorderQuantity: 'Reorder quantity',
      targetStock: 'Target stock',
      unitCost: 'Unit cost',
      preferredVendor: 'Preferred vendor',
      description: 'Description',
      created: 'Created',
    },

    /** Stock section. Single-facility companies never see a facility word. */
    stockHeading: 'Stock',
    stockByFacilityHeading: 'Stock by facility',
    transfer: 'Transfer between facilities',
    inStockLabel: 'In stock',
    reorderAtLabel: 'Reorder at',
    /** Owner, 2026-09-22: each facility owns its target; it does not inherit. */
    targetLabel: 'Target',
    binLabel: 'Bin',
    low: 'LOW',
    noFacilityStock: 'No stock recorded at any facility you can see.',
    colFacility: 'Facility',

    /** Recent movements. */
    ledgerHeading: 'Recent movements',
    ledgerEmpty: 'No stock movements recorded for this part yet.',
    ledgerScrollLabel: 'Recent stock movements',
    colWhen: 'When',
    colType: 'Type',
    colChange: 'Change',
    colBalance: 'Balance',
    colNote: 'Note',

    /** Vendors section. The register (owner, 2026-09-23: "convert them all to table views like we did with the others"). */
    vendorsHeading: 'Vendors',
    linkVendor: '+ Link vendor',
    vendorsEmpty: 'No vendors linked yet — link the suppliers this part is bought from.',
    preferred: 'Preferred',
    /** The four terms under a linked vendor, joined with ` · `. Each is a whole
     * phrase: `{d}d lead` had no space in English and a translator needs to be
     * able to add one. */
    supplierNumber: 'Supplier # {number}',
    leadTime: '{days}d lead',
    minimumOrder: 'MOQ {quantity}',
    unitCost: '${amount}/unit',
    noTerms: 'No terms recorded',
    editLink: 'Edit',
    removeLink: 'Remove',
    removeVendorFailed: 'Could not remove the vendor link.',
    /** Column headers, now that the four terms above are their own columns. */
    colSupplierNumber: 'Supplier #',
    colLeadTime: 'Lead time',
    colMinOrder: 'Min order qty',
    colUnitCost: 'Unit cost',
    vendorRowMenu: 'Actions for {vendor}',

    /** Where-used section. */
    whereUsedHeading: 'Where used',
    addToMachine: '+ Add to machine',
    whereUsedEmpty: 'Not on any machine’s BOM yet — add it to the equipment that uses it.',
    machineWithTag: '{name} ({assetTag})',
    perMachine: '{quantity} per machine',
    perMachineNotes: ' · {notes}',
    openEquipmentList: 'Open the equipment list filtered to this part →',
    bomFailed: 'Could not update the machine’s BOM.',
    colEquipment: 'Equipment',
    colQuantity: 'Quantity',
    colNotes: 'Notes',
    whereUsedRowMenu: 'Actions for {equipment}',
  },

  /** The link-a-vendor dialog on the part page. */
  /** F10.4 — the part page's Demand section. */
  demand: {
    heading: 'Demand',
    figure: { inStock: 'In stock', assigned: 'Assigned', committed: 'Committed', available: 'Available' },
    windowNote: 'Committed includes demand due within {days} days.',
    changeInSettings: 'Change in Settings',
    tableLabel: 'Where the demand comes from',
    colSource: 'From',
    colFacility: 'Facility',
    colDue: 'Due',
    colQuantity: 'Quantity',
    colCounts: 'Counts as',
    committed: 'Committed',
    assignedOnly: 'Assigned only',
    undated: 'No due date',
    forecastOf: 'Plan forecast: {plan}',
    empty: 'Nothing planned for this part.',
  },
  vendorLink: {
    addTitle: 'Link a vendor',
    editTitle: 'Edit vendor link',
    vendor: 'Vendor',
    choose: 'Choose…',
    supplierPartNumber: 'Supplier part #',
    leadTimeDays: 'Lead time (days)',
    minimumOrderQty: 'Minimum order qty',
    unitCost: 'Unit cost',
    isPreferred: 'Preferred vendor for this part',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    failed: 'Could not save the vendor link.',
  },

  /** Inter-facility transfer. */
  transfer: {
    title: 'Transfer stock between facilities',
    description: 'Writes an atomic transfer-out and transfer-in pair to the ledger.',
    from: 'From',
    to: 'To',
    chooseFacility: 'Choose a facility…',
    /** Was `(name) + ' — ' + String(n) + ' in stock'`. String concatenation
     * around a number, in a language that puts neither where English does. */
    facilityWithStock: '{facility} — {stock} in stock',
    facilityNoStock: '{facility} — no stock yet',
    quantity: 'Quantity',
    quantityWithMax: 'Quantity (max {max})',
    binAtDestination: 'Bin at destination (optional)',
    binPlaceholder: "Leave blank to keep the destination's current bin",
    note: 'Note (optional)',
    cancel: 'Cancel',
    transfer: 'Transfer',
    transferring: 'Transferring…',
    failed: 'Could not transfer stock.',
  },

  /** Add this part to a machine's BOM. */
  whereUsed: {
    addTitle: 'Add to a machine',
    editTitle: 'Edit where-used entry',
    equipment: 'Equipment',
    choose: 'Choose…',
    /** The option text: a machine's name, and its asset tag when it has one. */
    machineWithTag: '{name} ({assetTag})',
    quantityPerMachine: 'Quantity per machine',
    notes: 'Notes (optional)',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
  },

  /**
   * The catalogue-fields edit dialog.
   *
   * These were a `FieldSpec[]` MODULE CONSTANT — the second spelling of the
   * defect class this catalogue's header opens with. A `FIELDS` array feeding
   * `RecordEditDialog` is where a slice's field labels always end up, because
   * the copy rule cannot see a module constant and nobody edits the two
   * builders in the same sitting. `EditPartDialog` builds the array from these
   * words inside the component now.
   */
  editPart: {
    title: 'Edit {partNumber}',
    name: 'Name',
    description: 'Description',
    manufacturer: 'Manufacturer',
    category: 'Category',
    /** F12 */
    partType: 'Type',
    /**
     * Audit 08-12 C1. This is the CATALOG field, and it is not a bin at any
     * facility — the per-facility bin is edited on the part's Stock by
     * facility table. It read "Storage location / Bin or shelf.", which is
     * exactly how one generic value came to be treated as every plant's shelf.
     */
    storageLocation: 'Default storage location',
    /** -039: what the quantities count. The user's own word, never translated. */
    unitOfMeasure: 'Unit of measure',
    unitOfMeasureHelp: 'What a quantity counts — EA, kg, m, box. Shown beside every figure for this part.',
    storageLocationHelp:
      'A company-wide default, not a bin at any one facility. Set the real bin for each facility under Stock by facility.',
    minimumStock: 'Minimum stock',
    reorderPoint: 'Reorder point',
    reorderQuantity: 'Reorder quantity',
    unitCost: 'Unit cost',
    /** Fiix audit Phase 2: the part's photo, edited at the top of the dialog. */
    photo: 'Photo',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    failed: 'Could not save the part.',
    /** FF-AUDIT-005: a negative quantity or cost is refused beside its field. */
    notNegative: 'Must be 0 or more.',
    invalid: 'This value is not valid.',
  },

  /** One row of "Stock by facility", with its inline editor (audit WIR-10). */
  facilityStock: {
    low: 'LOW',
    edit: 'Edit',
    /** The screen-reader tail on the Edit button — a table of these otherwise
     * reads as ten identical "Edit"s. */
    editFor: ' reorder point, target and bin for {facility}',
    reorderPointAt: 'Reorder point at {facility}',
    reorderPointPlaceholder: 'Catalog default',
    /**
     * Owner decision, 2026-09-22: a target belongs to a FACILITY, and does
     * not inherit from the catalog — a threshold is a policy, a target is a
     * quantity, and quantities sum. So the placeholder says "none", not
     * "catalog default" like the reorder point above it.
     */
    targetAt: 'Target stock at {facility}',
    targetPlaceholder: 'No target',
    invalidTarget: 'Target stock must be a whole number of units, or blank for no target.',
    binAt: 'Bin location at {facility}',
    binPlaceholder: 'No bin',
    cancel: 'Cancel',
    save: 'Save',
    invalidReorderPoint:
      'Reorder point must be a whole number of units, or blank to use the catalog default.',
    failed: 'Could not save the facility settings.',
  },

  /** The spare-parts panel on an asset. */
  spareParts: {
    /** The register (owner, 2026-09-23: "convert them all to table views like we did with the others"). */
    region: 'Spare parts',
    colPartNumber: 'Part #',
    colName: 'Name',
    colPerAssembly: 'Per assembly',
    colStock: 'Stock',
    emptyTitle: 'No spare parts linked',
    emptyDescription: 'Link parts to this machine so the people fixing it know what fits.',
    /** The BOM link's name when the server sent none. */
    unnamedPart: 'Part',
    notStockedHere: 'Not stocked at this facility.',
    meterLabel: '{part} stock at this facility',
    meterNoTarget: '{stock} on hand, no target configured',
    meterOfTarget: '{stock} of {target} on hand',
    meterOfTargetLow: '{stock} of {target} on hand, at or below the reorder point',
    colAvailable: 'Available',
    colStatus: 'Status',
    /** Beside the figure, as on the Parts register. */
    ofTarget: 'of {target}',
    statusLow: 'Low stock',
    statusInStock: 'In stock',
    perAssembly: '{quantity} per assembly',
    perAssemblyNotes: ' · {notes}',
    deduct: 'Deduct stock',
    deductTitle: 'Deduct {part}',
    deductIntro: 'Records an append-only ledger movement against this machine.',
    deductOnHand: ' {stock} on hand at this facility.',
    unitsUsed: 'Units used',
    note: 'Note',
    notePlaceholder: 'What it was fitted to, or why',
    cancel: 'Cancel',
    deducting: 'Deducting…',
    invalidQuantity: 'Enter a whole number of units.',
    failed: 'The stock could not be deducted.',
  },

  /** Raise a re-order request (audit PRT-4). */
  requestReorder: {
    title: 'Request re-order — {part}',
    quantity: 'Quantity:',
    fromVendor: 'from {vendor}',
    facility: 'Facility',
    facilityDefault: 'Facility: my active facility',
    reason: 'Why do you need this?* (goes to the purchaser)',
    urgent: 'Urgent',
    cancel: 'Cancel',
    submit: 'Submit request',
    submitting: 'Submitting…',
    failed: 'Could not request.',
  },

  /** The published searchable part selector. */
  partPicker: {
    label: 'Part',
    placeholder: 'Search by part number or name…',
    /** The placeholder once a part is chosen, and the option row. */
    chosen: '{partNumber} — {name}',
    searchLabel: '{label} search',
    clear: 'Clear',
    searchFailed: 'Part search failed.',
    onHand: '{stock} on hand',
    /** FF-AUDIT-20260914-210459-033: the caller's facility first, the company total beside it. */
    onHandAtFacility: '{local} at this facility · {total} company-wide',
    /** F10.4 */
    availableSuffix: ' · {available} available',
    noMatches: 'No matching parts.',
  },
})

export type PartsCopy = typeof partsCopy.en
